import { skipToken } from "@reduxjs/toolkit/query";
import type { CustomFieldDependents } from "@repo/app-types/client/client";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { AlertDialog, AlertDialogVariant } from "@repo/ui/Modal/alertDialog";
import { CenteredModal } from "@repo/ui/Modal/centeredModal";
import { type FC, Fragment, type ReactNode, useEffect, useState } from "react";
import { useUserCustomFieldDependentsQuery } from "../../../api/entities/userCustomField.js";

export interface UserCustomFieldDefinitionDeleteDialogProps {
  customFieldDefinitionId: string | undefined;
  onConfirm: (customFieldDefinitionId: string) => void;
  onRequestClose: () => void;
}

export const UserCustomFieldDefinitionDeleteDialog: FC<
  UserCustomFieldDefinitionDeleteDialogProps
> = (props) => {
  const { customFieldDefinitionId } = props;
  const { currentData: dependents } = useUserCustomFieldDependentsQuery(
    customFieldDefinitionId ?? skipToken,
  );

  // Debounce so the loading state doesn't "flash."
  const [dependentsDebounced, setDependentsDebounced] = useState<
    CustomFieldDependents | undefined
  >(undefined);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDependentsDebounced(dependents);
    }, 500);

    return () => clearTimeout(timer);
  }, [dependents]);

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      props.onRequestClose();
    }
  };

  const handleConfirm = () => {
    if (customFieldDefinitionId) {
      props.onConfirm(customFieldDefinitionId);
    }

    props.onRequestClose();
  };

  const dependentItems: readonly ReactNode[] | undefined = dependentsDebounced
    ? [
        ...dependentsDebounced.message_templates.map(({ id, subject }) => (
          <Fragment key={id}>
            <strong>{subject}</strong> message template
          </Fragment>
        )),
        ...dependentsDebounced.form_fields.map(
          ({ field_id, form_name: formName, field_label: fieldLabel }) => (
            <Fragment key={field_id}>
              <strong>{fieldLabel}</strong> field of the{" "}
              <strong>{formName}</strong> form
            </Fragment>
          ),
        ),
        ...dependentsDebounced.playbook_outcomes.map(
          ({ playbook_id, name, is_voice_agent: isVoiceAgent }) => (
            <Fragment key={playbook_id}>
              Outcome of the <strong>{name}</strong>{" "}
              {isVoiceAgent ? "voice agent" : "playbook"}
            </Fragment>
          ),
        ),
      ]
    : undefined;

  return (
    <CenteredModal
      isOpen={customFieldDefinitionId !== undefined}
      onOpenChange={handleOpenChange}
      isDismissable
    >
      <AlertDialog
        variant={AlertDialogVariant.Warning}
        title="Delete custom field"
        confirmText="Delete custom field"
        onConfirm={dependentItems?.length === 0 ? handleConfirm : undefined}
      >
        {!dependentItems ? (
          <>
            <p>Checking whether this custom field is used anywhere...</p>
            <div className="my-2">
              <Loader style={LoaderStyle.ZOOMIES} />
            </div>
          </>
        ) : dependentItems.length > 0 ? (
          <>
            <p>
              This custom field cannot be deleted because it is still used by:
            </p>
            <ul className="my-2 list-disc space-y-1 pl-4">
              {dependentItems.map((item, index) => (
                <li key={index} className="">
                  {item}
                </li>
              ))}
            </ul>
          </>
        ) : (
          <p>
            Are you sure you want to delete this field? This action can't be
            undone.
          </p>
        )}
      </AlertDialog>
    </CenteredModal>
  );
};
